import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { VrModuleTitle } from '../layout'
import { Dropdown } from '../../../../legacy-design-system/dropdown'
import {
  HistogramViewSeverity,
  HistogramViewStatus,
  HistogramViewRuleType
} from './views'
import {
  VrThermometerHeader,
  VrThermometerHeaderLoadingView
} from '../vr-thermometer-header'
import { ErrorBoundary } from '../../../../legacy-design-system/error-boundary/error-boundary'
import { SelectedValueReportingFacet } from '../../../../graphql/schema/types/value-reporting'
import { VrModuleTitleLoadingView } from '../layout/vr-module-title'
import { VrHistogramViewLoading } from './views/vr-histogram-view'
import { DropdownLoadingView } from '../../../../legacy-design-system/dropdown/dropdown'
import {
  useVrApiExportDialog,
  VrApiExportDialog
} from '../../export/vr-api-export-dialog'
import { ValueReportingDashboardProps } from '../../vr-main'
import { getVulnerabilityHistogramCurlCommand } from '../../export/vr-curl-commands'
import { useVrVulnerabilities } from './use-vr-vulnerabilities'
import { VrStandaloneModulePanel } from '../layout/vr-module-containers'

const viewOptions: HistogramView[] = [
  {
    histogramType: 'SEVERITY',
    displayId: 'action_sortBySeverity'
  },
  {
    histogramType: 'STATUS',
    displayId: 'action_sortByStatus'
  },
  {
    histogramType: 'TYPE',
    displayId: 'action_sortByType'
  }
]

const defaultView: HistogramView = viewOptions[0]
interface HistogramView {
  histogramType: HistogramType
  displayId: string
}

const cssTitleDropdownContainer = css`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-right: 54px;
`

const Layout = ({
  renderDropdownRow,
  renderHistogramRow
}: {
  renderDropdownRow: () => React.ReactNode
  renderHistogramRow: () => React.ReactNode
}) => {
  return (
    <>
      <div className={cssTitleDropdownContainer}>{renderDropdownRow()}</div>
      {renderHistogramRow()}
    </>
  )
}

export type VrModuleVulnerabilityHistogramProps =
  ValueReportingDashboardProps & {
    selectedFacets: SelectedValueReportingFacet[]
  }

export function VrModuleVulnerabilityHistogram(
  props: VrModuleVulnerabilityHistogramProps
) {
  const { openDialog, dialogProps } = useVrApiExportDialog()
  const { organizationUuid, apiKey, authHeader, hostUrl, selectedFacets } =
    props

  return (
    <>
      <VrStandaloneModulePanel
        testId="value-reporting-vulnerabilities-module"
        exportButtonE2eTestId="value-reporting-vulnerabilities-export-button"
        hasBorder={true}
        order={3}
        onExportClick={openDialog}
        minHeight={300}
      >
        <ErrorBoundary>
          <HistogramLoader {...props} />
        </ErrorBoundary>
      </VrStandaloneModulePanel>
      <VrApiExportDialog
        {...dialogProps}
        titleId="title_vulnerabilityCurrentState"
        sections={[
          {
            sectionTestId: 'value-reporting-vulnerabilities',
            curlCommand: getVulnerabilityHistogramCurlCommand({
              protocolAndHost: hostUrl,
              organizationUuid,
              authHeader,
              apiKey,
              facets: selectedFacets
            })
          }
        ]}
        e2eTestId="value-reporting-vulnerabilities-dialog"
      />
    </>
  )
}

function HistogramLoader({
  organizationUuid,
  selectedFacets
}: ValueReportingDashboardProps & {
  selectedFacets: SelectedValueReportingFacet[]
}) {
  const intl = useIntl()

  const [view, setView] = useState<HistogramView>(defaultView)

  const { data, isLoading } = useVrVulnerabilities(
    organizationUuid,
    selectedFacets
  )

  // eslint-disable-next-line unicorn/prefer-ternary
  if (isLoading || !data) {
    return <LoadingView />
  } else {
    return (
      <Layout
        renderDropdownRow={() => (
          <>
            <VrModuleTitle id="title_vulnerabilities" />
            <Dropdown<HistogramView>
              e2eTestId="value-reporting-vulnerabilities-dropdown"
              items={viewOptions}
              itemToString={(item) =>
                intl.formatMessage({ id: item.displayId })
              }
              selectedItem={view}
              onSelect={setView}
            />
          </>
        )}
        renderHistogramRow={() => (
          <>
            <VrThermometerHeader
              e2eTestId="value-reporting-vulnerabilities-module-thermometer-header"
              i18nID={getTotalVulnerabilitiesLabelId(view.histogramType)}
              count={data.totalVulnerabilities}
            />
            <CurrentHistogram
              organizationUuid={organizationUuid}
              selectedFacets={selectedFacets}
              histogramType={view.histogramType}
              data={data.facets}
            />
          </>
        )}
      />
    )
  }
}

function getTotalVulnerabilitiesLabelId(histogramType: HistogramType): string {
  switch (histogramType) {
    case 'SEVERITY':
    case 'TYPE':
      return 'detail_totalOpenVulnerabilities'
    default:
      return 'detail_totalVulnerabilities'
  }
}

function LoadingView() {
  return (
    <Layout
      renderDropdownRow={() => (
        <>
          <VrModuleTitleLoadingView />
          <DropdownLoadingView />
        </>
      )}
      renderHistogramRow={() => (
        <>
          <VrThermometerHeaderLoadingView />
          <VrHistogramViewLoading total={5} />
        </>
      )}
    />
  )
}

export type HistogramType = 'SEVERITY' | 'STATUS' | 'TYPE'

const typeToComponentMap = {
  SEVERITY: HistogramViewSeverity,
  STATUS: HistogramViewStatus,
  TYPE: HistogramViewRuleType
}

function CurrentHistogram({
  organizationUuid,
  selectedFacets,
  histogramType,
  data
}: {
  organizationUuid: string
  selectedFacets: SelectedValueReportingFacet[]
  histogramType: HistogramType
  data: any
}) {
  const Component = typeToComponentMap[histogramType]

  if (!Component) {
    throw new Error(`unexpected vulnerability histogram type=${histogramType}`)
  }

  const props = {
    data,
    organizationUuid,
    selectedFacets,
    histogramType
  }

  return <Component {...props} />
}
